import React, { useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConsentDetails, ConsentType } from '../../constants/uaeStandards';
import { ConsentCard } from '../ui/ConsentCard';
import { SearchFilterBar, FilterOptions } from '../ui/SearchFilterBar';
import { getBankTheme, BankTheme } from '../../constants/bankThemes';
import { PlusIcon, ShieldCheckIcon, ListIcon } from 'lucide-react';
export interface LFIConsentManagementScreenProps {
  bankId: string;
  bankName: string;
  activeConsents: ConsentDetails[];
  pendingConsents: ConsentDetails[];
  historyConsents: ConsentDetails[];
  onViewDetails: (id: string) => void;
  onAuthorizeConsent: (id: string) => void;
  onRejectConsent: (id: string) => void;
  onRevokeConsent: (id: string) => void;
  onSuspendConsent: (id: string) => void;
}
type TabType = 'active' | 'pending' | 'history';
type ConsentTypeTab = 'all' | 'DataSharing' | 'ServiceInitiation';
export function LFIConsentManagementScreen({
  bankId,
  bankName,
  activeConsents,
  pendingConsents,
  historyConsents,
  onViewDetails,
  onAuthorizeConsent,
  onRejectConsent,
  onRevokeConsent,
  onSuspendConsent
}: LFIConsentManagementScreenProps) {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [consentTypeTab, setConsentTypeTab] = useState<ConsentTypeTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({});
  const bankTheme = getBankTheme(bankId) || {
    colors: {
      primary: '#1e3a5f',
      primaryDark: '#152b47',
      primaryLight: '#2a4a75'
    },
    gradients: {
      header: 'linear-gradient(135deg, #1e3a5f 0%, #152b47 100%)',
      button: 'linear-gradient(135deg, #1e3a5f 0%, #2a4a75 100%)'
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif'
    },
    borderRadius: '12px'
  };
  // Get unique TPP names for filter
  const availableTPPs = useMemo(() => {
    const tpps = new Set<string>();
    activeConsents.forEach(c => tpps.add(c.tppTradingName));
    pendingConsents.forEach(c => tpps.add(c.tppTradingName));
    historyConsents.forEach(c => tpps.add(c.tppTradingName));
    return Array.from(tpps).sort();
  }, [activeConsents, pendingConsents, historyConsents]);
  // Filter and search logic
  const filteredConsents = useMemo(() => {
    const consents = activeTab === 'active' ? activeConsents : activeTab === 'pending' ? pendingConsents : historyConsents;
    return consents.filter(consent => {
      // Search filter (by TPP name)
      if (searchQuery && !consent.tppTradingName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Consent type filter
      if (consentTypeTab !== 'all' && consent.type !== consentTypeTab) {
        return false;
      }
      // TPP name filter
      if (filters.bankName && consent.tppTradingName !== filters.bankName) {
        return false;
      }
      // Account number filter
      if (filters.accountNumber && !consent.accountLastFour.includes(filters.accountNumber)) {
        return false;
      }
      // Consent type filter (from dropdown)
      if (filters.consentType && consent.type !== filters.consentType) {
        return false;
      }
      // Consent state filter
      if (filters.consentState && consent.state !== filters.consentState) {
        return false;
      }
      return true;
    });
  }, [activeTab, consentTypeTab, searchQuery, filters, activeConsents, pendingConsents, historyConsents]);
  // Group by consent type
  const dataShareConsents = filteredConsents.filter(c => c.type === 'DataSharing');
  const serviceInitiationConsents = filteredConsents.filter(c => c.type === 'ServiceInitiation');
  const renderConsentList = (consents: ConsentDetails[], emptyMessage: string) => {
    if (consents.length === 0) {
      return <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center py-12" data-id="element-963">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{
          backgroundColor: bankTheme.colors.primary + '20'
        }} data-id="element-964">
            <ShieldCheckIcon className="w-8 h-8" style={{
            color: bankTheme.colors.primary
          }} data-id="element-965" />
          </div>
          <div className="text-base font-medium mb-2" style={{
          color: '#1A1A1A',
          fontFamily: bankTheme.fonts.body
        }} data-id="element-966">
            {emptyMessage}
          </div>
          <div className="text-sm" style={{
          color: '#6B7280'
        }} data-id="element-967">
            {activeTab === 'active' ? 'No TPPs currently have access to your accounts' : activeTab === 'pending' ? 'No pending consent requests' : 'Your consent history will appear here'}
          </div>
        </motion.div>;
    }
    return <motion.div initial="hidden" animate="show" variants={{
      hidden: {
        opacity: 0
      },
      show: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05
        }
      }
    }} className="space-y-3" data-id="element-968">
        {consents.map(consent => <motion.div key={consent.id} variants={{
        hidden: {
          opacity: 0,
          y: 20
        },
        show: {
          opacity: 1,
          y: 0
        }
      }} data-id="element-969">
            <ConsentCard consent={consent} onClick={() => onViewDetails(consent.id)} context="LFI" data-id="element-970" />
          </motion.div>)}
      </motion.div>;
  };
  return <div className="space-y-4 md:space-y-6" data-id="element-971">
      {/* Header - Responsive */}
      <div data-id="element-972">
        <h1 className="text-xl md:text-2xl font-bold mb-1" style={{
        fontFamily: bankTheme.fonts.heading,
        color: bankTheme.colors.primaryDark
      }} data-id="element-973">
          Open Finance Connected Services
        </h1>
        <p className="text-sm" style={{
        color: '#666666',
        fontFamily: bankTheme.fonts.body
      }} data-id="element-974">
          Manage TPP access to your {bankName} accounts
        </p>
      </div>

      {/* Main Tabs: Active, Pending, History - Responsive */}
      <div className="flex gap-1 sm:gap-2 border-b-2 overflow-x-auto" style={{
      borderColor: '#F3F4F6'
    }} data-id="element-975">
        {[{
        id: 'active' as TabType,
        label: 'Active',
        count: activeConsents.length
      }, {
        id: 'pending' as TabType,
        label: 'Pending',
        count: pendingConsents.length
      }, {
        id: 'history' as TabType,
        label: 'History',
        count: historyConsents.length
      }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="px-4 sm:px-6 py-3 font-semibold text-xs sm:text-sm transition-all relative whitespace-nowrap" style={{
        color: activeTab === tab.id ? bankTheme.colors.primary : '#9CA3AF',
        fontFamily: bankTheme.fonts.body
      }} data-id="element-976">
            <div className="flex items-center gap-2" data-id="element-977">
              <span data-id="element-978">{tab.label}</span>
              <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{
            backgroundColor: activeTab === tab.id ? bankTheme.colors.primary + '20' : '#F3F4F6',
            color: activeTab === tab.id ? bankTheme.colors.primary : '#9CA3AF'
          }} data-id="element-979">
                {tab.count}
              </span>
            </div>
            {activeTab === tab.id && <motion.div layoutId="lfiActiveTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5" style={{
          backgroundColor: bankTheme.colors.primary
        }} transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30
        }} data-id="element-980" />}
          </button>)}
      </div>

      {/* Search and Filter */}
      <SearchFilterBar onSearch={setSearchQuery} onFilterChange={setFilters} availableBanks={availableTPPs} context="LFI" data-id="element-981" />

      {/* Consent Type Tabs - Responsive with wrapping */}
      <div className="flex flex-wrap gap-2" data-id="element-982">
        {[{
        id: 'all' as ConsentTypeTab,
        label: 'All Consents',
        shortLabel: 'All',
        count: filteredConsents.length
      }, {
        id: 'DataSharing' as ConsentTypeTab,
        label: 'Data Sharing',
        shortLabel: 'Data',
        count: dataShareConsents.length
      }, {
        id: 'ServiceInitiation' as ConsentTypeTab,
        label: 'Service Initiation',
        shortLabel: 'Service',
        count: serviceInitiationConsents.length
      }].map(tab => <button key={tab.id} onClick={() => setConsentTypeTab(tab.id)} className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all" style={{
        backgroundColor: consentTypeTab === tab.id ? bankTheme.colors.primary : '#FFFFFF',
        color: consentTypeTab === tab.id ? 'white' : '#1A1A1A',
        borderRadius: bankTheme.borderRadius,
        border: `2px solid ${consentTypeTab === tab.id ? bankTheme.colors.primary : '#E5E7EB'}`
      }} data-id="element-983">
            <span className="hidden sm:inline" data-id="element-984">{tab.label}</span>
            <span className="sm:hidden" data-id="element-985">{tab.shortLabel}</span> ({tab.count})
          </button>)}
      </div>

      {/* Consent List */}
      <AnimatePresence mode="wait" data-id="element-986">
        <motion.div key={`${activeTab}-${consentTypeTab}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} data-id="element-987">
          {consentTypeTab === 'all' ? <>
              {dataShareConsents.length > 0 && <div className="mb-6" data-id="element-988">
                  <div className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wide" style={{
              color: '#666666',
              fontFamily: bankTheme.fonts.body
            }} data-id="element-989">
                    Data Sharing ({dataShareConsents.length})
                  </div>
                  {renderConsentList(dataShareConsents, 'No data sharing consents')}
                </div>}

              {serviceInitiationConsents.length > 0 && <div data-id="element-990">
                  <div className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wide" style={{
              color: '#666666',
              fontFamily: bankTheme.fonts.body
            }} data-id="element-991">
                    Service Initiation ({serviceInitiationConsents.length})
                  </div>
                  {renderConsentList(serviceInitiationConsents, 'No service initiation consents')}
                </div>}

              {filteredConsents.length === 0 && renderConsentList([], 'No consents found')}
            </> : consentTypeTab === 'DataSharing' ? renderConsentList(dataShareConsents, 'No data sharing consents') : renderConsentList(serviceInitiationConsents, 'No service initiation consents')}
        </motion.div>
      </AnimatePresence>

      {/* Security Notice */}
      <div className="p-4 rounded-xl" style={{
      backgroundColor: bankTheme.colors.primary + '10',
      border: `2px solid ${bankTheme.colors.primary}30`
    }} data-id="element-992">
        <div className="flex items-start gap-3" data-id="element-993">
          <ShieldCheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
          color: bankTheme.colors.primary
        }} data-id="element-994" />
          <div data-id="element-995">
            <div className="text-xs font-semibold mb-1" style={{
            color: bankTheme.colors.primaryDark
          }} data-id="element-996">
              Your Security
            </div>
            <div className="text-xs leading-relaxed" style={{
            color: bankTheme.colors.primaryDark
          }} data-id="element-997">
              You can revoke any TPP's access at any time. If you notice
              suspicious activity, contact {bankName} immediately.
            </div>
          </div>
        </div>
      </div>
    </div>;
}